<template>
  <div class="d-flex flex-column" style="gap: 24px; border-radius: 8px">
    <div class="content-block">
      <div ref="plotHeader">
        <v-row no-gutters align="center" justify="space-between">
          <div>
            <span class="body-bold-medium neutral-20-dark--text">
              {{ $t('message.pages.analyticsV3.livesPage.title') }}
            </span>
          </div>
          <span class="body-regular-smaller neutral-40-light--text">
            {{ $t('message.pages.analyticsV3.pages.devices.updatedAgo') }}
          </span>
        </v-row>
        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.livesPage.subtitle') }}
          <a
            class="body-bold-smaller neutral-30-base--text span-link"
            @click="handleGoToAnalyticsArticle"
          >
            {{ $t('message.commom.learnMore') }}
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>
      </div>

      <div class="chart-container" ref="chartsContainer">
        <v-row class="d-flex flex-wrap" align="stretch">
          <v-col
            v-for="cardItem in cardsItems"
            :key="cardItem.label"
            :cols="isMobileBreakpoint ? 12 : 4"
            class="d-flex"
          >
            <v-card
              flat
              class="d-flex flex-column justify-center align-center text-center fill-height custom-card-style"
            >
              <div v-if="!loading">
                <div class="d-flex align-center mb-1">
                  <span class="d-flex text-center body-label-caption neutral-40-light--text">
                    {{ cardItem.label }}
                    <v-tooltip v-if="cardItem.tooltip" max-width="200px" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-on="on"
                          v-bind="attrs"
                          size="12"
                          class="ml-1 neutral-70--text"
                          style="margin-bottom: 2px"
                        >
                          mdi-information
                        </v-icon>
                      </template>
                      <span>{{ cardItem.tooltip }}</span>
                    </v-tooltip>
                  </span>
                </div>
                <span class="neutral-20-dark--text body-bold-xlarge">
                  {{ cardItem.value }}
                </span>
              </div>

              <div v-else class="d-flex flex-column align-center">
                <v-skeleton-loader type="image" class="rounded-lg" width="84px" height="29px"/>
                <v-skeleton-loader type="image" class="mt-1 rounded-lg" width="32px" height="18px"/>
              </div>
            </v-card>
            </v-col>
        </v-row>
      </div>

      <v-input
        class="custom-search-bar"
        v-model="search"
        size="small"
        hide-details
        :placeholder="$t('message.pages.analyticsV3.livesPage.searchLives')"
      >
        <template v-slot:prepend-inner>
          <v-icon class="neutral-60--text">mdi-magnify</v-icon>
        </template>
      </v-input>

      <BaseTable
        :items="filteredLives"
        :columns="tableHeaders"
        :items-per-page="10"
        :show-pagination="true"
        :loading="tableLoading"
        :is-searching="search.length > 0"
        :showActionColumn="false"
        :showTotalLabel="true"
        :totalLabel="`${filteredLives.length} lives`"
        @clear-search="search = ''"
        class="mt-0"
      >
        <template #titleCell="{ row }">
          <span class="d-block text-truncate body-regular-small">
            {{ row.title }}
          </span>
        </template>
        <template #dateTimeCell="{ row }">
          <span class="body-regular-small">
            {{ formatDateTime(row.dateTime) }}
          </span>
        </template>
        <template #peakCell="{ row }">
          <span class="body-regular-small">
            {{ row.peak.toLocaleString() }}
          </span>
        </template>
        <template #viewersCell="{ row }">
          <span class="body-regular-small">
            {{ row.viewers.toLocaleString() }}
          </span>
        </template>
        <template #messagesCell="{ row }">
          <span class="body-regular-small">
            {{ row.messages.toLocaleString() }}
          </span>
        </template>
        <template #bandwidthCell="{ row }">
          <span class="body-regular-small">
            {{ row.bandwidth }}
          </span>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script>
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import { format } from 'date-fns'
import { mapState, mapMutations } from 'vuex'
import { exportToCsv } from '@/utils/csvExporter.js'
import { exportToPdf } from '@/utils/pdfExporter.js'
import { toPng } from 'html-to-image'

export default {
  name: 'Lives',
  components: {
    BaseTable,
  },
  data() {
    return {
      loading: true, 
      tableLoading: true, 
      search: '',
      isMobileBreakpoint: false,
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
      livesData: [
        { id: 1, title: 'Lançamento Produto X', dateTime: '2025-07-21T20:00:00Z', peak: 1250, viewers: 980, messages: 2340, bandwidth: '150.5 GB' },
        { id: 2, title: 'Webinar sobre IA', dateTime: '2025-07-18T15:30:00Z', peak: 800, viewers: 650, messages: 1500, bandwidth: '98.2 GB' },
        { id: 3, title: 'Q&A com a Diretoria', dateTime: '2025-07-15T10:00:00Z', peak: 2100, viewers: 1800, messages: 4500, bandwidth: '210.0 GB' },
        { id: 4, title: 'Treinamento de Vendas', dateTime: '2025-07-14T09:00:00Z', peak: 450, viewers: 400, messages: 730, bandwidth: '45.8 GB' },
        { id: 5, title: 'Webinar sobre IA', dateTime: '2025-07-18T15:30:00Z', peak: 800, viewers: 650, messages: 1500, bandwidth: '98.2 GB' },
        { id: 6, title: 'Q&A com a Diretoria', dateTime: '2025-07-15T10:00:00Z', peak: 2100, viewers: 1800, messages: 4500, bandwidth: '210.0 GB' },
        { id: 7, title: 'Treinamento de Vendas', dateTime: '2025-07-14T09:00:00Z', peak: 450, viewers: 400, messages: 730, bandwidth: '45.8 GB' },
        { id: 8, title: 'Webinar sobre IA', dateTime: '2025-07-18T15:30:00Z', peak: 800, viewers: 650, messages: 1500, bandwidth: '98.2 GB' },
        { id: 9, title: 'Q&A com a Diretoria', dateTime: '2025-07-15T10:00:00Z', peak: 2100, viewers: 1800, messages: 4500, bandwidth: '210.0 GB' },
        { id: 10, title: 'Treinamento de Vendas para textos muito maiores do que o necessário para ser considerado grande', dateTime: '2025-07-14T09:00:00Z', peak: 45880, viewers: 40000, messages: 17730, bandwidth: '45.8 GB' },
      ],
    }
  },
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
    }),
    summaryMetrics() {
      if (this.livesData.length === 0) {
        return { avgPeak: 0, avgViewers: 0, totalMessages: 0 }
      }
      const totalPeak = this.livesData.reduce((acc, live) => acc + live.peak, 0)
      const totalViewers = this.livesData.reduce((acc, live) => acc + live.viewers, 0)
      const totalMessages = this.livesData.reduce((acc, live) => acc + live.messages, 0)
      return {
        avgPeak: Math.round(totalPeak / this.livesData.length),
        avgViewers: Math.round(totalViewers / this.livesData.length),
        totalMessages: totalMessages,
      }
    },
    tableHeaders() {
      return [
        { 
          text: this.$t('message.pages.analyticsV3.livesPage.liveTitle'), 
          value: 'title', 
          align: 'start',
          width: '30%'
        },
        { text: this.$t('message.pages.analyticsV3.livesPage.dateTime'), 
          value: 'dateTime', 
          tooltip: this.$t(
            'message.pages.analyticsV3.livesPage.title'
          ), 
          align: 'end',
          width: '14%'
        },
        { text: this.$t('message.pages.analyticsV3.livesPage.peakOnly'), 
          value: 'peak',
          tooltip: this.$t(
            'message.pages.analyticsV3.livesPage.title'
          ),
          align: 'end',
          width: '14%' 
        },
        { text: this.$t('message.pages.analyticsV3.livesPage.viewersOnly'),
          value: 'viewers', 
          tooltip: this.$t(
            'message.pages.analyticsV3.livesPage.title'
          ), 
          align: 'end',
          width: '14%'
        },
        { text: this.$t('message.pages.analyticsV3.livesPage.messagesOnly'), 
          value: 'messages',
          tooltip: this.$t(
            'message.pages.analyticsV3.livesPage.title'
          ),
          align: 'end',
          width: '14%'
        },
        { text: this.$t('message.pages.analyticsV3.livesPage.bandWidth'), 
          value: 'bandwidth', 
          tooltip: this.$t(
            'message.pages.analyticsV3.livesPage.title'
          ),
          align: 'end',
          width: '14%'
        },
      ]
    },
    cardsItems() {
      const metrics = this.summaryMetrics
      return [ 
        { label: this.$t('message.pages.analyticsV3.livesPage.peak'), tooltip: this.$t('message.pages.analyticsV3.livesPage.tooltip'), value: metrics.avgPeak.toLocaleString() },
        { label: this.$t('message.pages.analyticsV3.livesPage.viewers'), tooltip: this.$t('message.pages.analyticsV3.livesPage.tooltip'), value: metrics.avgViewers.toLocaleString() },
        { label: this.$t('message.pages.analyticsV3.livesPage.messages'), tooltip: this.$t('message.pages.analyticsV3.livesPage.tooltip'), value: metrics.totalMessages.toLocaleString() },
      ]
    },
    filteredLives() {
      if (!this.search) {
        return this.livesData
      }
      const lowerCaseSearch = this.search.toLowerCase()
      return this.livesData.filter((live) =>
        live.title.toLowerCase().includes(lowerCaseSearch)
      )
    },
  },
  methods: {
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    formatDateTime(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    fetchData() {
      this.loading = true
      this.tableLoading = true
      setTimeout(() => {
        this.loading = false
        this.tableLoading = false
      }, 1000)
    },
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      exportToCsv(
        `analytics-lives-${startDateFormatted}--${endDateFormatted}.csv`,
        this.tableHeaders,
        this.filteredLives
      )
    },
    async generateAndDownloadPdf() {
      await this.$nextTick()
      const chartsElement = this.$refs.chartsContainer

      if (!chartsElement) {
        console.error('Elemento dos cards não encontrado para exportação.')
        return
      }

      chartsElement.classList.add('pdf-capture-mode')

      try {
        const imgDataUri = await toPng(chartsElement, {
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
        })

        const img = new Image()
        img.src = imgDataUri
        await new Promise((resolve) => {
          img.onload = resolve
        })

        const pdfImageWidth = 186
        const pdfImageHeight = (img.height * pdfImageWidth) / img.width

        const headerData = {
          title: this.$t('message.pages.analyticsV3.livesPage.title') || '', 
          description: this.$t('message.pages.analyticsV3.livesPage.subtitle') || '', 
        }

        const tableHeaders = this.tableHeaders.map((col) => col.text)
        const tableBody = this.filteredLives.map((item) =>
          this.tableHeaders.map((col) => {
            const value = item[col.value]
            switch (col.value) {
              case 'dateTime':
                return this.formatDateTime(value)
              case 'peak':
              case 'viewers':
              case 'messages':
                return value.toLocaleString()
              default:
                return value || '-'
            }
          })
        )
        
        const columnStyles = {
          0: { cellWidth: 'auto', overflow: 'ellipsize' }, 
          1: { cellWidth: 35 }, 
          2: { cellWidth: 25 }, 
          3: { cellWidth: 25 }, 
          4: { cellWidth: 25 }, 
          5: { cellWidth: 25 }, 
        }

        await exportToPdf({
          filename: 'relatorio-lives.pdf',
          chartHeaderData: headerData,
          htmlImageData: {
            uri: imgDataUri,
            width: pdfImageWidth,
            height: pdfImageHeight,
          },
          tableHeaders,
          tableBody,
          columnStyles,
        })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.UPDATE_ERROR_STATE(true) 
      } finally {
        chartsElement.classList.remove('pdf-capture-mode')
      }
    },
    async generateAndDownloadPng() {
      await this.$nextTick()
      const chartsElement = this.$refs.chartsContainer
      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }
      chartsElement.classList.add('pdf-capture-mode')
      try {
        const imgDataUri = await toPng(chartsElement, {
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
        })
        const link = document.createElement('a')
        link.download = 'lives.png'
        link.href = imgDataUri
        link.click()
      } catch (error) {
        console.error('Erro ao gerar PNG:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        chartsElement.classList.remove('pdf-capture-mode')
      }
    },
  },
  mounted() {
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-pdf-export', this.generateAndDownloadPdf)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.fetchData()
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-pdf-export', this.generateAndDownloadPdf)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
  },
}
</script>

<style lang="scss" scoped>
.content-block {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px 30px;
  gap: 24px;
  border-radius: 8px;
  background-color: var(--v-neutral-100-white-base);
}

.custom-card-style {
  width: 100%;
  background-color: varFigma(neutral-99);
  padding: 16px;
  border-radius: 12px;
  height: 200px; 
}

.chart-container .v-row {
  margin: 0 -8px;
}
.chart-container .v-col {
  padding: 8px;
}

.custom-search-bar {
  width: 100%;
  ::v-deep .v-input__slot {
    height: 40px; 
  }

  ::v-deep .v-label {
    background-color: varFigma(neutral-100-white) !important;
    color: varFigma(neutral-95-lightest) !important;
    padding: 0 4px !important;
  }

  ::v-deep .v-input__icon--prepend-inner .v-icon {
    color: varFigma(neutral-95-lightest) !important;
    font-size: 3rem !important;
  }

  &.v-input--is-focused {
    ::v-deep fieldset {
      border-color: varFigma(neutral-60) !important;
    }

    ::v-deep .v-label {
      color: varFigma(neutral-60) !important;
    }
  }
}

.pdf-capture-mode {
  width: 950px;
  height: 220px;
  &,
  * {
    transition: none !important;
    animation: none !important;
    box-shadow: none !important;
    border: none !important;
    outline: 0 !important;
  }
  .text-truncate {
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
  }
  * {
    text-overflow: clip !important;
    overflow: visible !important;
  }
}
</style>